import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { SMSAction } from "../components/actions/sms";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";
import {
  SMSActionFormValues,
  smsActionFormSchema
} from "../components/actions/action-form-values";
import { useLocale } from "@hooks";
import { BulkActionCommunicationSubType, SMS_MAX_GSM7_LENGTH } from "@bonliva-crm/shared";
import { useBulkSmsTextMaxLengthQuery } from "@bonliva-crm/graphql";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantSMS"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
  subType?: BulkActionCommunicationSubType | null;
};

export const useConsultantSMSAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const { valueParser, selectedRows, totalRows, entitySingular, entityPlural, subType } =
    props;
  const classes = useStyles();
  const { locale } = useLocale();
  const [smsTotalCost, setSmsTotalCost] = useState("");

  const parsedValues = useMemo(
    () => valueParser(selectedRows),
    [valueParser, selectedRows]
  );

  // The API owns the limit: it is lower when the worker appends the
  // unsubscribe link, and the enqueue check uses the same number.
  const { data: maxLengthData } = useBulkSmsTextMaxLengthQuery({
    variables: { subType: subType ?? null },
    fetchPolicy: "cache-and-network"
  });
  const maxLength = maxLengthData?.bulkSmsTextMaxLength ?? SMS_MAX_GSM7_LENGTH;
  const maxLengthRef = useRef(maxLength);
  maxLengthRef.current = maxLength;

  const smsActionForm = useForm<SMSActionFormValues>({
    defaultValues: {
      recipients: parsedValues.recipients,
      message: null
    },
    mode: "onChange",
    resolver: (values, context, options) =>
      yupResolver(smsActionFormSchema(maxLengthRef.current))(values, context, options)
  });

  const { sendConsultantSMS } = useBulkActions({});

  const actionContent = (
    <SMSAction
      form={smsActionForm}
      setSmsTotalCost={setSmsTotalCost}
      maxLength={maxLength}
    />
  );

  const rowsWithValidCellPhone = parsedValues.recipients.length;

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      {totalRows !== rowsWithValidCellPhone && (
        <Typography variant="body1">
          {locale.bulkActionSMSNotValid(
            totalRows,
            rowsWithValidCellPhone,
            entitySingular,
            entityPlural
          )}
        </Typography>
      )}
      <Typography variant="body1">
        {locale.bulkActionConfirmation(
          `${locale.SendSMS} ${locale.To}`,
          rowsWithValidCellPhone,
          entitySingular,
          entityPlural
        )}
      </Typography>
      <Typography variant="body1">{locale.smsTotalCostText(smsTotalCost)}</Typography>
      <Typography variant="body1">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );

  const onSubmit = smsActionForm.handleSubmit((data) =>
    sendConsultantSMS({
      message: data.message,
      consultantIds: parsedValues.nodeIds,
      subType: subType ?? undefined
    })
  );

  const reset = () => {
    smsActionForm.reset();
  };

  const nextStepDisabled = !smsActionForm.formState.isValid;

  // Re-check a message typed before the limit arrived.
  useEffect(() => {
    if (smsActionForm.getValues("message")) {
      smsActionForm.trigger("message");
    }
  }, [maxLength, smsActionForm]);

  useEffect(() => {
    smsActionForm.setValue("recipients", parsedValues.recipients);
  }, [parsedValues.recipients, smsActionForm]);

  return {
    actionContent,
    confirmationContent,
    onSubmit,
    reset,
    nextStepDisabled
  };
};

const useStyles = makeStyles((theme) => ({
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }
}));
